/**
 * useFormSchemas Hook
 *
 * React Query hook for CRUD operations on form_schemas table.
 * Used by Form Analyzer admin UI.
 *
 * Operations:
 * - Fetch all schemas with optional filters
 * - Fetch single schema by ID
 * - Create new schema
 * - Update schema fields/metadata
 * - Approve schema (with optional template creation)
 * - Delete schema
 * - Track field edits for audit
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';
import { supabase } from '@/lib/supabase';
import type { ParsedFormField } from '@/types/smart-schema';

// =============================================================================
// TYPES
// =============================================================================

export type SchemaStatus = 'draft' | 'analyzing' | 'review' | 'approved' | 'archived';
export type ParsedBy = 'ai' | 'manual' | 'ai_reviewed';

export interface FormSchemaRow {
  id: string;
  form_id: string;
  form_name: string;
  jurisdiction_code: string;
  permit_type: string;
  version: string;
  fields: ParsedFormField[];
  sections: Array<{ id: string; name: string; order: number; fields: string[] }>;
  metadata: {
    pageCount: number;
    totalFields: number;
    aiConfidenceAvg: number;
    needsReview: string[];
  };
  status: SchemaStatus;
  parsed_by: ParsedBy;
  pdf_storage_path: string | null;
  pdf_filename: string | null;
  form_template_id: string | null;
  ai_analysis_id: string | null;
  ai_model_version: string | null;
  ai_confidence_avg: number | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
  created_at: string;
  updated_at: string;
  approved_at: string | null;
}

export interface CreateSchemaInput {
  form_id: string;
  form_name: string;
  jurisdiction_code: string;
  permit_type: string;
  version?: string;
  fields?: ParsedFormField[];
  sections?: Array<{ id: string; name: string; order: number; fields: string[] }>;
  metadata?: Partial<FormSchemaRow['metadata']>;
  status?: SchemaStatus;
  parsed_by?: ParsedBy;
  pdf_storage_path?: string;
  pdf_filename?: string;
}

export interface UpdateSchemaInput {
  form_name?: string;
  jurisdiction_code?: string;
  permit_type?: string;
  version?: string;
  fields?: ParsedFormField[];
  sections?: Array<{ id: string; name: string; order: number; fields: string[] }>;
  metadata?: Partial<FormSchemaRow['metadata']>;
  status?: SchemaStatus;
}

export interface ApproveSchemaInput {
  schemaId: string;
  reviewerId: string;
  reviewNotes?: string;
  createTemplate?: boolean;
}

export interface FieldEditInput {
  schema_id: string;
  field_id: string;
  action: 'add' | 'remove' | 'rename' | 'change_type' | 'change_semantic' | 'toggle_required' | 'merge' | 'split';
  previous_value?: unknown;
  new_value?: unknown;
  edited_by?: string;
  reason?: string;
}

export interface FetchSchemasOptions {
  status?: SchemaStatus;
  jurisdictionCode?: string;
  permitType?: string;
  parsedBy?: ParsedBy;
  needsReview?: boolean;
}

// =============================================================================
// QUERY KEYS
// =============================================================================

export const formSchemaKeys = {
  all: ['form-schemas'] as const,
  lists: () => [...formSchemaKeys.all, 'list'] as const,
  list: (filters: FetchSchemasOptions) => [...formSchemaKeys.lists(), filters] as const,
  details: () => [...formSchemaKeys.all, 'detail'] as const,
  detail: (id: string) => [...formSchemaKeys.details(), id] as const,
  stats: () => [...formSchemaKeys.all, 'stats'] as const,
  needingReview: () => [...formSchemaKeys.all, 'needing-review'] as const,
};

// =============================================================================
// FETCH FUNCTIONS
// =============================================================================

async function fetchSchemas(options: FetchSchemasOptions = {}): Promise<FormSchemaRow[]> {
  let query = supabase
    .from('form_schemas')
    .select('*')
    .order('updated_at', { ascending: false });

  if (options.status) {
    query = query.eq('status', options.status);
  }
  if (options.jurisdictionCode) {
    query = query.eq('jurisdiction_code', options.jurisdictionCode);
  }
  if (options.permitType) {
    query = query.eq('permit_type', options.permitType);
  }
  if (options.parsedBy) {
    query = query.eq('parsed_by', options.parsedBy);
  }
  if (options.needsReview) {
    query = query.or('status.eq.review,status.eq.draft,ai_confidence_avg.lt.0.7');
  }

  const { data, error } = await query;

  if (error) throw error;
  return data as FormSchemaRow[];
}

async function fetchSchemaById(id: string): Promise<FormSchemaRow | null> {
  const { data, error } = await supabase
    .from('form_schemas')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    throw error;
  }
  return data as FormSchemaRow;
}

async function fetchSchemasNeedingReview(): Promise<FormSchemaRow[]> {
  const { data, error } = await supabase.rpc('get_schemas_needing_review');

  if (error) throw error;
  return data as FormSchemaRow[];
}

// =============================================================================
// MUTATION FUNCTIONS
// =============================================================================

async function createSchema(input: CreateSchemaInput): Promise<FormSchemaRow> {
  const { data, error } = await supabase
    .from('form_schemas')
    .insert({
      form_id: input.form_id,
      form_name: input.form_name,
      jurisdiction_code: input.jurisdiction_code,
      permit_type: input.permit_type,
      version: input.version || '1.0',
      fields: input.fields || [],
      sections: input.sections || [],
      metadata: {
        pageCount: 0,
        totalFields: input.fields?.length || 0,
        aiConfidenceAvg: 0,
        needsReview: [],
        ...input.metadata,
      },
      status: input.status || 'draft',
      parsed_by: input.parsed_by || 'manual',
      pdf_storage_path: input.pdf_storage_path,
      pdf_filename: input.pdf_filename,
    })
    .select()
    .single();

  if (error) throw error;
  return data as FormSchemaRow;
}

async function updateSchema(
  id: string,
  input: UpdateSchemaInput
): Promise<FormSchemaRow> {
  const updateData: Record<string, unknown> = {};

  if (input.form_name !== undefined) updateData.form_name = input.form_name;
  if (input.jurisdiction_code !== undefined) updateData.jurisdiction_code = input.jurisdiction_code;
  if (input.permit_type !== undefined) updateData.permit_type = input.permit_type;
  if (input.version !== undefined) updateData.version = input.version;
  if (input.fields !== undefined) {
    updateData.fields = input.fields;
    // Recalculate metadata
    updateData.metadata = {
      totalFields: input.fields.length,
      aiConfidenceAvg: input.fields.length > 0
        ? input.fields.reduce((sum, f) => sum + f.confidence, 0) / input.fields.length
        : 0,
      needsReview: input.fields
        .filter(f => f.confidence < 0.6 || f.semanticId === 'unknown')
        .map(f => f.pdfField),
    };
  }
  if (input.sections !== undefined) updateData.sections = input.sections;
  if (input.status !== undefined) updateData.status = input.status;

  const { data, error } = await supabase
    .from('form_schemas')
    .update(updateData)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as FormSchemaRow;
}

async function approveSchema(input: ApproveSchemaInput): Promise<FormSchemaRow> {
  const { data, error } = await supabase.rpc('approve_form_schema', {
    p_schema_id: input.schemaId,
    p_reviewer_id: input.reviewerId,
    p_review_notes: input.reviewNotes || null,
    p_create_template: input.createTemplate || false,
  });

  if (error) throw error;
  return data as FormSchemaRow;
}

async function deleteSchema(id: string): Promise<void> {
  const { error } = await supabase
    .from('form_schemas')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

async function trackFieldEdit(input: FieldEditInput): Promise<void> {
  const { error } = await supabase
    .from('schema_field_edits')
    .insert({
      schema_id: input.schema_id,
      field_id: input.field_id,
      action: input.action,
      previous_value: input.previous_value,
      new_value: input.new_value,
      edited_by: input.edited_by,
      reason: input.reason,
    });

  if (error) throw error;
}

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Fetch all form schemas with optional filters
 */
export function useFormSchemas(options: FetchSchemasOptions = {}) {
  return useQuery({
    queryKey: formSchemaKeys.list(options),
    queryFn: () => fetchSchemas(options),
  });
}

/**
 * Fetch a single form schema by ID
 */
export function useFormSchema(id: string | undefined) {
  return useQuery({
    queryKey: formSchemaKeys.detail(id || ''),
    queryFn: () => fetchSchemaById(id!),
    enabled: !!id,
  });
}

/**
 * Fetch schemas needing review (low confidence or review status)
 */
export function useSchemasNeedingReview() {
  return useQuery({
    queryKey: formSchemaKeys.needingReview(),
    queryFn: fetchSchemasNeedingReview,
  });
}

/**
 * Create a new form schema
 */
export function useCreateSchema() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createSchema,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: formSchemaKeys.lists() });
    },
  });
}

/**
 * Update an existing form schema
 */
export function useUpdateSchema() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateSchemaInput }) =>
      updateSchema(id, input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: formSchemaKeys.lists() });
      queryClient.setQueryData(formSchemaKeys.detail(data.id), data);
    },
  });
}

/**
 * Approve a form schema
 */
export function useApproveSchema() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: approveSchema,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: formSchemaKeys.lists() });
      queryClient.invalidateQueries({ queryKey: formSchemaKeys.needingReview() });
      queryClient.setQueryData(formSchemaKeys.detail(data.id), data);
    },
  });
}

/**
 * Delete a form schema
 */
export function useDeleteSchema() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteSchema,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: formSchemaKeys.lists() });
      queryClient.invalidateQueries({ queryKey: formSchemaKeys.needingReview() });
    },
  });
}

/**
 * Track a field edit (for audit purposes)
 */
export function useTrackFieldEdit() {
  return useMutation({
    mutationFn: trackFieldEdit,
  });
}

/**
 * Update a single field within a schema
 */
export function useUpdateSchemaField() {
  const queryClient = useQueryClient();
  const trackEdit = useTrackFieldEdit();

  return useMutation({
    mutationFn: async ({
      schemaId,
      fieldId,
      updates,
      userId,
    }: {
      schemaId: string;
      fieldId: string;
      updates: Partial<ParsedFormField>;
      userId?: string;
    }) => {
      // Fetch current schema
      const schema = await fetchSchemaById(schemaId);
      if (!schema) throw new Error('Schema not found');

      // Find and update the field
      const fieldIndex = schema.fields.findIndex(f => f.pdfField === fieldId);
      if (fieldIndex === -1) throw new Error('Field not found');

      const oldField = schema.fields[fieldIndex];
      const newField = { ...oldField, ...updates };
      const newFields = [...schema.fields];
      newFields[fieldIndex] = newField;

      // Track the edit
      if (updates.semanticId !== undefined && updates.semanticId !== oldField.semanticId) {
        await trackEdit.mutateAsync({
          schema_id: schemaId,
          field_id: fieldId,
          action: 'change_semantic',
          previous_value: oldField.semanticId,
          new_value: updates.semanticId,
          edited_by: userId,
        });
      }
      if (updates.dataType !== undefined && updates.dataType !== oldField.dataType) {
        await trackEdit.mutateAsync({
          schema_id: schemaId,
          field_id: fieldId,
          action: 'change_type',
          previous_value: oldField.dataType,
          new_value: updates.dataType,
          edited_by: userId,
        });
      }
      if (updates.required !== undefined && updates.required !== oldField.required) {
        await trackEdit.mutateAsync({
          schema_id: schemaId,
          field_id: fieldId,
          action: 'toggle_required',
          previous_value: oldField.required,
          new_value: updates.required,
          edited_by: userId,
        });
      }

      // Update the schema
      return updateSchema(schemaId, { fields: newFields });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: formSchemaKeys.lists() });
      queryClient.setQueryData(formSchemaKeys.detail(data.id), data);
    },
  });
}

// =============================================================================
// AI ANALYSIS (Cloud Function)
// =============================================================================

export interface AnalyzeFormPdfInput {
  pdfBase64: string;
  jurisdictionCode: string;
  permitType: string;
  formName: string;
  saveToDatabase?: boolean;
}

export interface AnalyzeFormPdfResult {
  success: boolean;
  schema: {
    formId: string;
    formName: string;
    jurisdictionCode: string;
    permitType: string;
    version: string;
    parsedAt: string;
    parsedBy: 'ai';
    aiModelVersion: string;
    fields: ParsedFormField[];
    sections: Array<{ id: string; name: string; order: number; fields: string[] }>;
    metadata: {
      pageCount: number;
      totalFields: number;
      aiConfidenceAvg: number;
      needsReview: string[];
      formNumber?: string;
      processingTimeMs: number;
    };
  };
  schemaId: string | null;
  stats: {
    totalFields: number;
    highConfidence: number;
    mediumConfidence: number;
    lowConfidence: number;
    needsReview: number;
    processingTimeMs: number;
  };
}

/**
 * Analyze a PDF form using AI vision
 * Calls the analyzeFormPdf Cloud Function
 */
export function useAnalyzeFormPdf() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: AnalyzeFormPdfInput): Promise<AnalyzeFormPdfResult> => {
      const analyzeFormPdf = httpsCallable<AnalyzeFormPdfInput, AnalyzeFormPdfResult>(
        functions,
        'analyzeFormPdf'
      );
      const result = await analyzeFormPdf(input);
      return result.data;
    },
    onSuccess: (data) => {
      // If saved to database, invalidate queries
      if (data.schemaId) {
        queryClient.invalidateQueries({ queryKey: formSchemaKeys.lists() });
        queryClient.invalidateQueries({ queryKey: formSchemaKeys.needingReview() });
      }
    },
  });
}

/**
 * Get field suggestions based on label text
 * Calls the getFieldSuggestions Cloud Function
 */
export function useGetFieldSuggestions() {
  return useMutation({
    mutationFn: async (label: string): Promise<Array<{ semanticId: string; score: number; category: string }>> => {
      const getFieldSuggestions = httpsCallable<{ label: string }, { suggestions: Array<{ semanticId: string; score: number; category: string }> }>(
        functions,
        'getFieldSuggestions'
      );
      const result = await getFieldSuggestions({ label });
      return result.data.suggestions;
    },
  });
}
